/**
 * The customer's dashboard: an MSME, or an individual.
 *
 * It answers four things in the order the business cares about them: what is
 * happening to my requests, who can see my data, how ready am I, and what
 * would move me. Readiness is shown with its own caveat attached rather than
 * as a score on its own, because a number that looks like a credit rating and
 * is not one will be read as one.
 */
import { Link } from 'react-router-dom'

import type { Dict, Grant, SectionMeta } from '../../api/types'
import { useSession } from '../../app/session'
import { appPath } from '../../app/paths'
import { Badge, Empty, Meter, Note, toneOf } from '../../components/bits'
import { PageHead } from '../../components/PageHead'
import { Panel, Cols } from '../../components/Panel'
import { TileStrip } from './common'
import type { TileData } from './common'

export function CustomerDashboard({ data, section, grant }: {
  data: Dict; section: SectionMeta; grant?: Grant
}) {
  const { n, amount, d, dt, lbl, can } = useSession()
  const requests = (data.requests || []) as Dict[]
  const offers = (data.offers || []) as Dict[]
  const grants = (data.grants || []) as Dict[]
  const readiness = data.readiness as Dict | null
  const completeness = data.completeness as Dict | null
  const disclosures = (data.disclosures || {}) as Dict
  const plan = data.plan as Dict | null

  return (
    <>
      <PageHead section={section} grant={grant}
                actions={can('funding.request_new', 'create')
                  ? [{ label: 'Raise a request', href: '/app/requests/new',
                       style: 'primary' }]
                  : []} />
      <TileStrip tiles={(data.tiles || []) as TileData[]} />

      <Cols>
        <Panel title="Your profile" explain="profile.completeness">
          {completeness ? (
            <>
              <div className="big-value">
                <b className="num">{n(completeness.completeness_pct, 0)}%</b>
                <span className="muted">complete</span>
              </div>
              <Meter pct={Number(completeness.completeness_pct)} />
              <p className="xs muted">
                {n(completeness.verified_pct, 0)}% of what is held comes from an
                authoritative source rather than from something you typed.
                Verified attributes carry higher confidence in an assessment, and
                a missing attribute is never read as a failure.
              </p>
              <Link className="btn sm" to={appPath('/app/profile')}>Open your profile</Link>
            </>
          ) : (
            <Empty title="No profile has been built yet">
              A profile assembles itself from the registries once you consent to
              them being read.
            </Empty>
          )}
        </Panel>

        <Panel title="Readiness" explain="readiness.assessment">
          {readiness ? (
            <>
              <div className="big-value">
                <b className="num">{n(readiness.score, 1)}</b>
                <span className="muted">
                  {lbl(readiness.band || readiness.readiness_band)}
                </span>
              </div>
              <p className="xs muted">
                Assessed {readiness.assessed_at ? dt(readiness.assessed_at) : '—'}.
              </p>
              <Note kind="warn">
                <b>This is a model output, not a credit decision.</b> It is
                calibrated on fewer real outcomes than it needs to be reliable,
                and no provider sees it. It exists to tell you what would move
                you, not to rank you.
              </Note>
            </>
          ) : (
            <Empty title="Not assessed yet">
              An assessment needs enough of the profile to say something useful.
            </Empty>
          )}
        </Panel>
      </Cols>

      <Panel title={`Your requests · ${requests.length}`} explain="funding.my_requests" tight>
        {requests.length === 0 ? (
          <Empty title="No request is open">
            Nothing is disclosed to any provider until you raise a request and
            grant consent for it.
          </Empty>
        ) : (
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th>Reference</th><th className="num">Amount</th><th>Purpose</th>
                  <th>Compliance</th><th>State</th><th className="num">Applications</th>
                  <th className="num">Offers</th><th>Raised</th><th className="acts" />
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={String(r.request_id)}>
                    <td data-label="Reference">
                      <span className="mono clip">{r.request_ref as string}</span>
                    </td>
                    <td data-label="Amount" className="num">
                      {amount(r.amount_requested, r.currency as string)}
                    </td>
                    <td data-label="Purpose">{lbl(r.economic_purpose)}</td>
                    <td data-label="Compliance">
                      {r.compliance_required
                        ? <Badge tone={toneOf(r.compliance_required)}>
                            {lbl(r.compliance_required)}
                          </Badge>
                        : <span className="muted">either</span>}
                    </td>
                    <td data-label="State">
                      <Badge tone={toneOf(r.status)}>{lbl(r.status)}</Badge>
                    </td>
                    <td data-label="Applications" className="num">{n(r.applications)}</td>
                    <td data-label="Offers" className="num">{n(r.offers)}</td>
                    <td data-label="Raised">{d(r.created_at)}</td>
                    <td className="acts">
                      <Link className="btn sm" to={appPath(`/app/requests/${r.request_id}`)}>
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {offers.length > 0 && (
        <Panel title={`Offers on the table · ${offers.length}`} explain="offer.list" tight>
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th>Provider</th><th className="num">Amount</th>
                  <th className="num">Total cost of finance</th><th className="num">Tenor</th>
                  <th>State</th><th>Expires</th><th className="acts" />
                </tr>
              </thead>
              <tbody>
                {offers.map((o) => (
                  <tr key={String(o.offer_id)}>
                    <td data-label="Provider">{o.provider_name as string}</td>
                    <td data-label="Amount" className="num">
                      {amount(o.amount_offered, o.currency_code as string)}
                    </td>
                    <td data-label="Total cost of finance" className="num">
                      {amount(o.total_cost_of_finance, o.currency_code as string)}
                    </td>
                    <td data-label="Tenor" className="num">{n(o.tenor_months)} mo</td>
                    <td data-label="State">
                      <Badge tone={toneOf(o.status)}>{lbl(o.status)}</Badge>
                    </td>
                    <td data-label="Expires">{o.expires_at ? d(o.expires_at) : '—'}</td>
                    <td className="acts">
                      {Boolean(o.request_id) && (
                        <Link className="btn sm"
                              to={appPath(`/app/requests/${o.request_id}/compare`)}>
                          Compare
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="xs muted">
            Cost is the total cost of finance on one published method, so two
            offers structured differently are comparable. A charge that only
            arises if something goes wrong is excluded and stated separately
            rather than averaged into the figure.
          </p>
        </Panel>
      )}

      <Cols>
        <Panel title={`Who may see your data · ${grants.length}`} explain="consent.my_grants" tight>
          {grants.length === 0 ? (
            <Empty title="You have granted nothing">
              No provider can read anything about you.
            </Empty>
          ) : (
            <div className="tbl-wrap">
              <table className="dl">
                <thead>
                  <tr><th>Who</th><th>For what</th><th>Until</th><th className="acts" /></tr>
                </thead>
                <tbody>
                  {grants.map((g) => (
                    <tr key={String(g.consent_id)}>
                      <td data-label="Who">{g.consumer_name as string}</td>
                      <td data-label="For what">{lbl(g.purpose_code)}</td>
                      <td data-label="Until">
                        {g.expires_at ? d(g.expires_at) : <span className="muted">open</span>}
                      </td>
                      <td className="acts">
                        <Link className="btn sm"
                              to={appPath(`/app/consents/${g.consent_id}`)}>Open</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="What has actually been read" explain="consent.disclosures">
          <div className="big-value">
            <b className="num">{n(disclosures.total)}</b>
            <span className="muted">disclosures</span>
          </div>
          <p className="xs muted">
            {n(disclosures.denied)} further attempts were refused by the consent
            gateway. Last read{' '}
            {disclosures.last_access ? dt(disclosures.last_access) : 'never'}.
          </p>
          <p className="xs muted">
            Every read is logged, by whom and under which consent and for which
            purpose. A refusal is logged as well, which is what lets this count
            be checked rather than believed.
          </p>
        </Panel>
      </Cols>

      {plan && (
        <Panel title="What would move you" explain="readiness.plan">
          <p>{(plan.summary as string) || 'A remediation plan has been drawn up.'}</p>
          <Link className="btn sm" to={appPath('/app/readiness')}>Open the plan</Link>
        </Panel>
      )}
    </>
  )
}
